import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import db from '../../db/db';
import { setAlert } from '../../actions/alert';

const Asistencias = () => {
    const dispatch = useDispatch();

    const [empleadoLogueado, setEmpleadoLogueado] = useState(null);
    const [eventoActivo, setEventoActivo] = useState(null);
    const [corteActivo, setCorteActivo] = useState(null);
    const [paseActivo, setPaseActivo] = useState(null);
    const [fecha] = useState(new Date());
    const [empleados, setEmpleados] = useState([]);
    const [statusGuardar, setStatusGuardar] = useState(false);
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        const init = async () => {
            // Paso 1 — Sesión
            const sesion = await db.sesion.toArray();
            const logueado = sesion[0];
            setEmpleadoLogueado(logueado);

            // Paso 2 — Sucursal y evento activo
            const sucursalId = logueado.sucursal?._id || logueado.sucursal;
            const sucursalReg = await db.sucursales
                .filter(s => String(s._id || s.id) === String(sucursalId))
                .first();
            setEventoActivo(sucursalReg?.evento || null);

            const empleadosSucursal = await db.empleados
                .filter(e => {
                    const ids = Array.isArray(e.sucursales) && e.sucursales.length > 0
                        ? e.sucursales.map(s => String(s?._id || s))
                        : [String(e.sucursal?._id || e.sucursal)];
                    return ids.includes(String(sucursalId));
                })
                .toArray();

            // Paso 3 — Verificar corte activo
            const corte = await db.cortes
                .filter(c => c.status === false && String(c.sucursal?._id || c.sucursal) === String(sucursalId))
                .first();

            if (!corte) {
                setCargando(false);
                return;
            }

            setCorteActivo(corte);

            // Paso 4 — Detectar pase vinculado al corte activo
            const paseExistente = await db.asistencias
                .filter(a => a.corte_local_id === corte.id
                    && String(a.sucursal?._id || a.sucursal) === String(sucursalId))
                .first();

            if (paseExistente) {
                setPaseActivo(paseExistente);
                // Pre-llenar con los valores guardados, completar con empleados que no estén
                const ids = paseExistente.empleados.map(e => String(e.empleado));
                const filas = empleadosSucursal.map(emp => {
                    const guardado = paseExistente.empleados.find(
                        e => String(e.empleado) === String(emp._id)
                    );
                    return {
                        empleado_id: emp._id,
                        nombre: emp.nombre,
                        apellido_paterno: emp.apellido_paterno || '',
                        apellido_materno: emp.apellido_materno || '',
                        asistio: guardado ? guardado.asistio : false,
                    };
                });
                // Agregar empleados del pase que ya no estén en sucursal local
                paseExistente.empleados.forEach(e => {
                    if (!ids.includes(String(e.empleado)) && !filas.find(f => String(f.empleado_id) === String(e.empleado))) {
                        filas.push({ empleado_id: e.empleado, nombre: e.nombre || '—', apellido_paterno: '', apellido_materno: '', asistio: e.asistio });
                    }
                });
                setEmpleados(filas);
            } else {
                setEmpleados(
                    empleadosSucursal.map(emp => ({
                        empleado_id: emp._id,
                        nombre: emp.nombre,
                        apellido_paterno: emp.apellido_paterno || '',
                        apellido_materno: emp.apellido_materno || '',
                        asistio: false,
                    }))
                );
            }
        };

        init().finally(() => setCargando(false));
    }, []);

    const handleToggle = (empleado_id) => {
        setEmpleados(prev =>
            prev.map(e => e.empleado_id === empleado_id ? { ...e, asistio: !e.asistio } : e)
        );
    };

    const handleGuardar = async () => {
        setStatusGuardar(true);

        const registro = {
            sucursal:       empleadoLogueado.sucursal?._id,
            responsable:    empleadoLogueado._id,
            evento:         eventoActivo?._id || null,
            corte_local_id: corteActivo?.id || null,
            fecha:          new Date(),
            empleados:      empleados.map(e => ({
                empleado: e.empleado_id,
                asistio:  e.asistio,
            })),
        };

        try {
            if (paseActivo) {
                await db.asistencias.update(paseActivo.id, registro);
            } else {
                const nuevoId = await db.asistencias.add(registro);
                setPaseActivo({ ...registro, id: nuevoId });
            }
            dispatch(setAlert('Pase de lista guardado correctamente', 'success'));
        } catch {
            dispatch(setAlert('Error al guardar el pase de lista', 'danger'));
        } finally {
            setStatusGuardar(false);
        }
    };

    const fechaLarga = fecha.toLocaleDateString('es-MX', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });

    const asistentes = empleados.filter(e => e.asistio).length;

    if (cargando) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 120px)' }}>
            <span className="spinner-border" style={{ color: '#F5C800' }} role="status" />
        </div>
    );

    if (!corteActivo) return (
        <div style={styles.page}>
            <div style={styles.encabezado}>
                <div>
                    <div style={styles.encabezadoTitulo}>
                        <i className="fas fa-clipboard-check me-2" style={{ color: '#F5C800' }}></i>
                        Pase de lista
                    </div>
                </div>
            </div>
            <div style={styles.bloqueado}>
                <i className="fas fa-lock" style={{ fontSize: '2.5rem', color: '#444', marginBottom: '1rem' }}></i>
                <p style={{ color: '#888', margin: 0, fontSize: '0.95rem', textAlign: 'center', lineHeight: 1.6 }}>
                    No hay un corte de caja activo.<br />
                    Inicia un corte antes de realizar el pase de lista.
                </p>
            </div>
        </div>
    );

    return (
        <div style={styles.page}>
            {/* Encabezado */}
            <div style={styles.encabezado}>
                <div>
                    <div style={styles.encabezadoTitulo}>
                        <i className="fas fa-clipboard-check me-2" style={{ color: '#F5C800' }}></i>
                        Pase de lista
                    </div>
                    <div style={styles.fechaLabel}>
                        {fechaLarga.charAt(0).toUpperCase() + fechaLarga.slice(1)}
                    </div>
                </div>
                <div style={styles.resumenBadge}>
                    <span style={styles.resumenNum}>{asistentes}</span>
                    <span style={styles.resumenTotal}>/ {empleados.length}</span>
                    <span style={styles.resumenLabel}>presentes</span>
                </div>
            </div>

            {/* Lista de empleados */}
            <div style={styles.card}>
                {empleados.length === 0 ? (
                    <div style={styles.vacio}>
                        <i className="fas fa-user-slash" style={{ fontSize: '2rem', color: '#444', marginBottom: '0.75rem' }}></i>
                        <p style={{ color: '#888', margin: 0 }}>No hay empleados en esta sucursal.</p>
                    </div>
                ) : (
                    empleados.map((emp, idx) => (
                        <div
                            key={emp.empleado_id}
                            style={{
                                ...styles.fila,
                                borderBottom: idx < empleados.length - 1 ? '1px solid #222' : 'none',
                                background: emp.asistio ? 'rgba(245,200,0,0.04)' : 'transparent',
                            }}
                            onClick={() => handleToggle(emp.empleado_id)}
                        >
                            <div style={styles.empleadoInfo}>
                                <div style={{
                                    ...styles.avatar,
                                    background: emp.asistio ? 'rgba(245,200,0,0.15)' : '#222',
                                    border: emp.asistio ? '2px solid rgba(245,200,0,0.4)' : '2px solid #333',
                                }}>
                                    <i className="fas fa-user" style={{ color: emp.asistio ? '#F5C800' : '#555', fontSize: '1rem' }}></i>
                                </div>
                                <span style={{ ...styles.nombreEmpleado, color: emp.asistio ? '#F0F0F0' : '#888' }}>
                                    {emp.nombre} {emp.apellido_paterno} {emp.apellido_materno}
                                </span>
                            </div>
                            <div style={{
                                ...styles.check,
                                background: emp.asistio ? '#F5C800' : 'transparent',
                                border: emp.asistio ? '2px solid #F5C800' : '2px solid #444',
                            }}>
                                {emp.asistio && (
                                    <i className="fas fa-check" style={{ color: '#111', fontSize: '0.9rem' }}></i>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Botón guardar */}
            <button
                style={statusGuardar || empleados.length === 0
                    ? { ...styles.btnGuardar, opacity: 0.6, cursor: 'not-allowed' }
                    : styles.btnGuardar
                }
                onClick={handleGuardar}
                disabled={statusGuardar || empleados.length === 0}
            >
                {statusGuardar ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                        Guardando...
                    </>
                ) : (
                    <>
                        <i className="fas fa-save me-2"></i>
                        {paseActivo ? 'Actualizar pase de lista' : 'Guardar pase de lista'}
                    </>
                )}
            </button>
        </div>
    );
};

const styles = {
    page: {
        maxWidth: '680px',
        margin: '0 auto',
    },
    encabezado: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1.25rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #2E2E2E',
    },
    encabezadoTitulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        fontSize: '1.2rem',
        marginBottom: '0.25rem',
    },
    fechaLabel: {
        color: '#888888',
        fontSize: '0.9rem',
    },
    resumenBadge: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '12px',
        padding: '0.5rem 1rem',
        textAlign: 'center',
        minWidth: '80px',
    },
    resumenNum: {
        color: '#F5C800',
        fontWeight: 800,
        fontSize: '1.4rem',
    },
    resumenTotal: {
        color: '#555',
        fontWeight: 700,
        fontSize: '1.1rem',
        marginLeft: '2px',
    },
    resumenLabel: {
        display: 'block',
        color: '#666',
        fontSize: '0.75rem',
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        overflow: 'hidden',
        marginBottom: '1.25rem',
    },
    vacio: {
        padding: '3rem',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    bloqueado: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        padding: '3rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    fila: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem 1.25rem',
        cursor: 'pointer',
        transition: 'background 0.15s',
        userSelect: 'none',
    },
    empleadoInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.9rem',
    },
    avatar: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 0.2s',
    },
    nombreEmpleado: {
        fontWeight: 600,
        fontSize: '1rem',
        transition: 'color 0.2s',
    },
    check: {
        width: '28px',
        height: '28px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 0.2s',
    },
    btnGuardar: {
        background: '#F5C800',
        color: '#111111',
        border: 'none',
        borderRadius: '12px',
        padding: '1rem',
        fontWeight: 800,
        fontSize: '1.05rem',
        width: '100%',
        cursor: 'pointer',
        transition: 'background 0.2s',
    },
};

export default Asistencias;
